import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { freeze } from '@reduxjs/toolkit';
import {
  DashboardTemplateScope,
  IGetDashboardTemplatesByScopeQuery,
  IGetDashboardTemplatesByScopeQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { GET_DASHBOARD_TEMPLATES_BY_SCOPE } from '../../../../utils/apollo/dashboards';
import { useDashboardEntityPermissionCheck } from '../../../../utils/hooks/permissionHooks/useDashboardEntityPermission';
import useUser from '../../../../utils/hooks/useUser';
import { FavoriteStarIcon } from '../../../Icon';
import Avatar from '../../../Avatar';
import { ReactComponent as PublishedIndicator } from '../../../../assets/images/published-indicator.svg';
import { NEW_DASHBOARD_SETTINGS } from './utils';
import { CustomOption } from './types';

const NEW_DASHBOARD_OPTION = freeze<CustomOption>(
  {
    value: NEW_DASHBOARD_SETTINGS.key,
    label: NEW_DASHBOARD_SETTINGS.optionLabel,
  },
  true,
);

const DASHBOARD_TEMPLATE_QUERY_VARIABLES =
  freeze<IGetDashboardTemplatesByScopeQueryVariables>(
    {
      term: '',
      scopes: [
        // Order will be reflected on UI
        DashboardTemplateScope.Favorite,
        DashboardTemplateScope.CreatedByMe,
        DashboardTemplateScope.Published,
      ],
    },
    true,
  );

const useAllDashboardTemplateOptions = () => {
  const user = useUser();
  const userPicture = user.picture;
  const { getDashboardEntityPermissions } = useDashboardEntityPermissionCheck();

  const { data, loading } = useQuery<
    IGetDashboardTemplatesByScopeQuery,
    IGetDashboardTemplatesByScopeQueryVariables
  >(GET_DASHBOARD_TEMPLATES_BY_SCOPE, {
    variables: DASHBOARD_TEMPLATE_QUERY_VARIABLES,
  });

  const templatesData = data?.dashboardTemplatesByScope;

  const options = useMemo<CustomOption[]>(() => {
    const items: CustomOption[] = [NEW_DASHBOARD_OPTION];

    if (!templatesData) {
      return items;
    }

    templatesData.forEach((templateGroup) => {
      if (
        templateGroup.scope === DashboardTemplateScope.Favorite &&
        templateGroup.entities.length > 0
      ) {
        items.push({
          value: 'favoriteDashboards',
          label: 'Favorite Dashboards',
          subOptions: templateGroup.entities.map((template) => {
            const { dashboardEntityPermissions } =
              getDashboardEntityPermissions(template);

            return {
              value: template.baseId,
              label: template.name,
              isDisabled: !dashboardEntityPermissions.addReport,
            };
          }),
          icon: <FavoriteStarIcon isActive={true} />,
        });
      }

      if (
        templateGroup.scope === DashboardTemplateScope.CreatedByMe &&
        templateGroup.entities.length > 0
      ) {
        items.push({
          value: 'myDashboards',
          label: 'My Dashboards',
          subOptions: templateGroup.entities.map((template) => ({
            value: template.baseId,
            label: template.name,
          })),
          icon: <Avatar picture={userPicture} size={16} />,
        });
      }

      if (
        templateGroup.scope === DashboardTemplateScope.Published &&
        templateGroup.entities.length > 0
      ) {
        items.push({
          value: 'publishedDashboards',
          label: 'Published Dashboards',
          subOptions: templateGroup.entities.map((template) => {
            const { dashboardEntityPermissions } =
              getDashboardEntityPermissions(template);

            return {
              value: template.baseId,
              label: template.name,
              isDisabled: !dashboardEntityPermissions.addReport,
            };
          }),
          icon: <PublishedIndicator width={16} height={16} />,
        });
      }
    });

    return items;
  }, [getDashboardEntityPermissions, templatesData, userPicture]);

  return {
    loading,
    options,
  };
};

export default useAllDashboardTemplateOptions;
